<template>
  <label class="switch" :style="{ ...switchStyle, ...cssVars }">
    <input type="checkbox" v-model="state_local" @change="changeHandler($event?.target)" />
    <span class="slider round" :style="sliderStyle"></span>
  </label>
</template>

<script setup lang="ts">
import { HTMLInputElement } from "happy-dom";
import { ref, onMounted, watch, computed } from "vue";
interface Props {
  state: boolean;
  no_local_state?: boolean;
  size?: number;
}

const emit = defineEmits(["change"]);
const props = withDefaults(defineProps<Props>(), { size: 2 });
const state_local = ref(false);

// Base dimensions (for size=2)
const baseWidth = 60;
const baseHeight = 34;
const baseSliderSize = 26;
const baseBorderRadius = 34;

// Computed styles based on size prop
const switchStyle = computed(() => {
  const scale = props.size / 2;
  return {
    width: `${baseWidth * scale}px`,
    height: `${baseHeight * scale}px`,
  };
});

const sliderStyle = computed(() => {
  const scale = props.size / 2;
  return {
    borderRadius: `${baseBorderRadius * scale}px`,
  };
});

// Computed CSS variables for dynamic styling
const cssVars = computed(() => {
  const scale = props.size / 2;
  return {
    "--slider-size": `${baseSliderSize * scale}px`,
    "--slider-offset": `${4 * scale}px`,
    "--slider-translate": `${baseSliderSize * scale}px`,
  };
});

const changeHandler = async (value: EventTarget | null) => {
  const newValue = (value as any as HTMLInputElement).checked;
  if (props.no_local_state) {
    state_local.value = props.state;
  } else {
    state_local.value = newValue;
  }
  emit("change", newValue);
};
watch(
  () => props.state,
  () => {
    state_local.value = props.state;
  }
);
onMounted(() => {
  state_local.value = props.state;
});
</script>

<style lang="css" scoped>
/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  /* Width and height are set dynamically via switchStyle */
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  /* Border radius is set dynamically via sliderStyle */
}

.slider:before {
  position: absolute;
  content: "";
  height: var(--slider-size, 26px);
  width: var(--slider-size, 26px);
  left: var(--slider-offset, 4px);
  bottom: var(--slider-offset, 4px);
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196f3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(var(--slider-translate, 26px));
  -ms-transform: translateX(var(--slider-translate, 26px));
  transform: translateX(var(--slider-translate, 26px));
}

/* Rounded sliders */
.slider.round {
  /* Border radius is set dynamically via sliderStyle */
}

.slider.round:before {
  border-radius: 50%;
}
</style>
